import React, { useEffect } from "react";
import { useStore } from "../../store/store";
import { useRequiredMsgs } from "../../store/requiredMsgs";
import { useTheme } from "@mui/material";
import { screenImg, screenPocketImg } from "./screen.biz";

const odds = ["3L OR 3R", "5L OR 5R", "7L OR 7R"];

function useScreenSelector(
  widerThan16: boolean,
  widerThanLimitForPicoSwingSingleScreen: boolean,
  noSinglePocktRange: boolean,
  isPocketAllowed: boolean
) {
  const theme = useTheme();
  const addToQuote = useStore((state) => state.addToQuote);
  const material = useStore((state) => state.quote.material);
  const doorType = useStore((state) => state.quote.doorType);
  const screen = useStore((state) => state.quote.addScreen);
  const screenVal = screen && screen.replace(" (upcharge applies)", "");

  const screenLayout = useStore((state) => state.quote.screenLayout);
  const screenColorChoice = useStore((state) => state.quote.screenColor);
  const cornerType = useStore((state) => state.quote.cornerType);

  const screenPocket = useStore((state) => state.quote.screenPocket);

  const screenPocketVal =
    screenPocket && screenPocket.replace(" (upcharge applies)", "");

  const isDoubleScreenSelected = screenVal && screenVal.includes("Double");

  const swingType = useStore((state) => state.quote.swingType);
  const isSwingPair = doorType === "Swing" && swingType === "Pair Swing";
  const isSingleSwing = doorType === "Swing" && swingType === "Single Swing";

  const stiles = useStore((state) => state.quote.stileCategory);

  const bifoldConfiguration = useStore((state) => state.quote.configuration);
  const exactConfig = useStore((state) => state.quote.exactConfig);

  const handleErrMsgChange = useStore((state) => state.handleError);

  const oneSideOddConfig =
    doorType.includes("Bi-fold") && odds.includes(bifoldConfiguration || "");

  const oneDirectionConfig =
    isSingleSwing && exactConfig.slice(-2) === "LH"
      ? "sL"
      : isSingleSwing && exactConfig.slice(-2) === "RH"
        ? "sR"
        : doorType.includes("Bi-fold") && exactConfig.length === 2
          ? exactConfig
          : "";

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const isCornerUnit = doorType?.includes("Corner");
  const isComboUnit = doorType?.includes("Combo");

  const isBifoldAlum =
    material === "All Aluminum" &&
    (doorType === "Bi-fold" || doorType === "Bi-fold Window");

  const isBifoldCombo = doorType === "Bi-fold Door/Window Combo";

  const isSwing = doorType === "Swing";

  const isPico = stiles?.includes("Pico");

  const isPicoSwing = isSwing && isPico;

  const isScreenNotRequired = isBifoldAlum || isBifoldCombo;

  const screenImgShown = screenImg(
    isCornerUnit,
    cornerType,
    isDoubleScreenSelected || false,
    screenLayout ? screenLayout : "",
  );

  const screenPocketImgShown = screenPocketImg(
    isCornerUnit,
    oneSideOddConfig || false,
    oneDirectionConfig ? oneDirectionConfig : "",
    isDoubleScreenSelected || false,
    screenLayout ? screenLayout : "",
    screenPocketVal ? screenPocketVal : "",
  );

  useEffect(() => {
    if (isDoubleScreenSelected && oneSideOddConfig && oneDirectionConfig)
      defaultPocketWide();
    if (!isDoubleScreenSelected && oneDirectionConfig && oneDirectionConfig[1])
      defaultLayout();
  }, [isDoubleScreenSelected, oneSideOddConfig, oneDirectionConfig]);

  useEffect(() => {
    if ((isCornerUnit && cornerType) || isComboUnit) {
      if (widerThan16 && screenVal) {
        return addToQuote({
          addScreen: "",
          screenPocket: "",
        });
      }
    } else if (isPicoSwing && widerThanLimitForPicoSwingSingleScreen && !isDoubleScreenSelected) {
      return addToQuote({
        addScreen: "",
        screenPocket: "",
      });
    }
  }, [cornerType, widerThan16, widerThanLimitForPicoSwingSingleScreen]);

  //reseting screen pocket field if pico is not allowed
  useEffect(() =>{
    if(!isPocketAllowed && screenPocketVal){
      addToQuote({ screenPocket: "" });
    }
  }, [isPocketAllowed])

  const defaultLayout = () => {
    if (!oneDirectionConfig || !oneDirectionConfig[1]) return;
    if (oneDirectionConfig[1] === "L") {
      addToQuote({ screenLayout: "Single Compress Left" });
    }
    if (oneDirectionConfig[1] === "R") {
      addToQuote({ screenLayout: "Single Compress Right" });
    }
  };

  const defaultPocketWide = () => {
    if (!isDoubleScreenSelected) return;
    const pocketSide =
      oneDirectionConfig[1] === "L"
        ? "Add Pocket Right"
        : oneDirectionConfig[1] === "R"
          ? "Add Pocket Left"
          : "Add Pocket";

    addToQuote({ screenPocket: pocketSide });
  };

  const handleAddScreenClick = (label: string) => {
    const checked = label === screenVal;

    handleErrMsgChange("jambErr", "");

    if (checked) {
      addToQuote(
        {
          addScreen: "",
          jamb: "",
          screenPocket: ""
        },
        "addScreen",
      );
    } else {
      if (findRequired("Add Screen")) {
        removeRequired("Add Screen");
      }
      if (findRequired("Brio Retractable Screen")) {
        removeRequired("Brio Retractable Screen");
      }

      let update = {
        addScreen: label,
        jamb: "",
        screenPocket: "",
      };

      // if (!label?.includes("Double") && noSinglePocktRange && screenPocket) {
      //   update["screenPocket"] = "";
      // }

      addToQuote(update, "addScreen");
    }
  };

  const handleLayoutClick = (label: string) => {
    const checked = label === screenLayout;

    if (checked) {
      addToQuote({
        screenLayout: "",
      });
    } else {
      addToQuote({
        screenLayout: label,
      });

      if (findRequired("Screen Layout")) removeRequired("Screen Layout");
    }
  };

  const handleScreenColorClick = (label: string) => {
    const checked = label === screenColorChoice;
    const requiredKey = "Screen Extrusion Color";

    if (checked) {
      addToQuote({ screenColor: "" });
    } else {
      addToQuote({ screenColor: label });

      if (findRequired(requiredKey)) removeRequired(requiredKey);
    }
  };

  return {
    handleScreenColorClick,
    handleLayoutClick,
    handleAddScreenClick,
    theme,
    isCornerUnit,
    isComboUnit,
    isDoubleScreenSelected,
    stiles,
    isSwingPair,
    doorType,
    screenVal,
    screenLayout,
    screenColorChoice,
    oneDirectionConfig,
    oneSideOddConfig,
    defaultPocketWide,
    screenImgShown,
    screenPocketImgShown,
    isScreenNotRequired,
    isPicoSwing,
  };
}

export default useScreenSelector;
